import { delimitWithCommas, pluralise } from '@lib/utils';

interface ConditionFacet {
  count: number;
  isRefined: boolean;
  label: string;
}

interface MakeModelFacet extends Omit<ConditionFacet, 'label' | 'count'> {
  escapedValue: string;
}

export function buildMakeModelLabel<T extends MakeModelFacet>({
  makes,
  models,
}: {
  makes: T[];
  models: T[];
}): string | null {
  const singleMakeSelected =
    makes.filter((item) => item.isRefined).length === 1;
  const singleModelSelected =
    models.filter((item) => item.isRefined).length === 1;

  if (singleMakeSelected) {
    const selectedMake = makes.find((item) => item.isRefined)!.escapedValue;

    if (singleModelSelected) {
      const selectedModel = models.find((item) => item.isRefined)!.escapedValue;
      return `${selectedMake} ${selectedModel.split(' > ')[1]}`;
    }

    return selectedMake;
  }

  return null;
}

export function buildPaymentFilterLabel({
  highestPrice,
  lowestPrice,
  paymentType,
  selectedMax,
  selectedMin,
}: {
  paymentType: string;
  highestPrice?: number;
  lowestPrice?: number;
  selectedMax?: number;
  selectedMin?: number;
}) {
  const suffix = paymentType === 'monthlyPayment' ? ' per month' : '';
  const min = selectedMin !== lowestPrice ? selectedMin : undefined;
  const max = selectedMax !== highestPrice ? selectedMax : undefined;

  if (min === max && min !== undefined)
    return `£${delimitWithCommas(min)}${suffix}`;
  if (min && max)
    return `Between £${delimitWithCommas(min)} and £${delimitWithCommas(
      max
    )}${suffix}`;
  if (min) return `Over £${delimitWithCommas(min)}${suffix}`;
  if (max) return `Under £${delimitWithCommas(max)}${suffix}`;
  return '';
}

export const formattedCondition = (condition?: string): string => {
  if (!condition) return '';
  if (condition === 'Motability' || condition === 'Value Range')
    return condition;
  return condition.toLowerCase();
};

export function getVehicleType(selectedCondition?: string) {
  if (selectedCondition === 'vans') return 'van';
  if (selectedCondition === 'motorbikes') return 'motorbike';

  return 'car';
}

export function buildCurrentSearchLabel<
  T extends ConditionFacet,
  S extends MakeModelFacet
>({
  conditions,
  makes,
  models,
  nbHits,
  isMotability = false,
}: {
  conditions: T[];
  makes: S[];
  models: S[];
  nbHits: number;
  isMotability?: boolean;
}) {
  const selectedCondition = conditions.find(
    (item) =>
      item.isRefined ||
      (item.count === nbHits && ['Vans', 'Motorbikes'].includes(item.label))
  )?.label;

  const condition = isMotability
    ? 'Motability'
    : formattedCondition(selectedCondition);

  const count = delimitWithCommas(nbHits);

  const vehicleType = getVehicleType(condition);

  const makeModel = buildMakeModelLabel({ makes, models });

  const pluralisedVehicleType = pluralise(nbHits, vehicleType);

  const includeCondition = !['van', 'motorbike'].includes(vehicleType);

  const labelComponentsInOrder = [
    count,
    includeCondition && condition,
    makeModel,
    pluralisedVehicleType,
  ].filter(Boolean);

  return `${labelComponentsInOrder.join(' ')} for sale`;
}

export function onlyExcludedValuesSelected<
  T extends { value: string | number },
  S extends {
    attribute: string;
    refinements: T[];
  }
>(items: S[], attribute: string, values: (string | number)[]) {
  const refinements =
    items.find((item) => item.attribute === attribute)?.refinements || [];

  return (
    refinements.length === values.length &&
    refinements.every((item) => values.includes(item.value))
  );
}
